import { Markdown } from '@/src/components/common/Markdown';
import { JamConstants } from '@/src/constants/shared/jam-constants';
import { usePolling } from '@/src/hooks/usePolling';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { JamArenaVerdictStatus, JamArenaWriteUp } from '@/src/types/JamChallengeDetails';
import { getErrorCode, getErrorMessage } from '@/src/utils/errors.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { LoggingService } from '@/src/utils/logging-service.utils';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  FormField,
  Header,
  ProgressBar,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  StatusIndicatorProps,
  Tabs,
  Textarea,
} from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ArenaTab = 'brief' | 'writeUp' | 'verdict';

const isConflict = (e: unknown) => getErrorCode(e) === 409 || /409/.test(getErrorMessage(e));

const WRITE_UP_FIELDS: { field: keyof JamArenaWriteUp; testId: string }[] = [
  { field: 'architecture', testId: 'arena-architecture' },
  { field: 'decisions', testId: 'arena-decisions' },
  { field: 'requirementsCoverage', testId: 'arena-requirements-coverage' },
];

/**
 * Arena panel for SCENARIO challenges: brief, design write-up, and the Judge's verdict,
 * one tab each. Replaces the task list on the challenge details page.
 */
const JamArenaPanel: React.FC = () => {
  const { t } = useTranslation();
  const { eventName } = useJamEventDetails();
  const { addErrorFlashbar, addSuccessFlashbar } = useFlashbars();
  const {
    selectedJamChallenge,
    isSelectedChallengeStarted,
    arenaWriteUpState,
    arenaSubmitResult,
    arenaVerdict,
    isSavingArenaWriteUp,
    isSubmittingArena,
    saveArenaWriteUp,
    submitArena,
    loadArenaVerdict,
  } = useJamChallengeDetails();

  const challengeId = selectedJamChallenge?.id ?? '';
  const arena = selectedJamChallenge?.arena;
  const projection = arena?.projection;
  const team = arena?.team;

  const [writeUp, setWriteUp] = useState<JamArenaWriteUp>({});
  const [dirty, setDirty] = useState(false);
  // Returning teams land on their verdict; first-timers start with the brief.
  const [activeTab, setActiveTab] = useState<ArenaTab>((team?.submissionCount ?? 0) > 0 ? 'verdict' : 'brief');

  // Seed the editor from the server's copy: the last save, else what loadChallenge delivered.
  // Re-seeds after a conflict refresh (arenaWriteUpState cleared) so teammates' edits appear.
  const serverWriteUp = arenaWriteUpState?.writeUp ?? team?.writeUp;
  useEffect(() => {
    setWriteUp({
      architecture: serverWriteUp?.architecture ?? '',
      decisions: serverWriteUp?.decisions ?? '',
      requirementsCoverage: serverWriteUp?.requirementsCoverage ?? '',
    });
    setDirty(false);
  }, [serverWriteUp?.architecture, serverWriteUp?.decisions, serverWriteUp?.requirementsCoverage, challengeId]);

  // A submission from an earlier page load may still be judging: pick it up from team state.
  useEffect(() => {
    if (team && team.submissionCount > 0 && !arenaVerdict && challengeId && eventName) {
      void loadArenaVerdict(eventName, challengeId);
    }
  }, [team?.submissionCount, challengeId, eventName]);

  const isPending = arenaVerdict?.status === JamArenaVerdictStatus.PENDING;
  usePolling({
    enabled: isPending && !!challengeId,
    intervalMs: JamConstants.ARENA_VERDICT_POLL_INTERVAL_MS,
    load: () => loadArenaVerdict(eventName, challengeId),
    logLabel: 'arenaVerdict',
  });

  const update = (field: keyof JamArenaWriteUp) => (value: string) => {
    setWriteUp((previous) => ({ ...previous, [field]: value }));
    setDirty(true);
  };

  const onSave = async () => {
    try {
      await saveArenaWriteUp(eventName, challengeId, writeUp);
      addSuccessFlashbar(t(i18nKeys.myJams.challenges.details.arena.writeUp.saved));
    } catch (e) {
      if (isConflict(e)) {
        addErrorFlashbar(t(i18nKeys.myJams.challenges.details.arena.writeUp.conflict));
      }
      LoggingService.debug('Arena write-up save failed', e);
    }
  };

  const onSubmit = async () => {
    try {
      if (dirty) {
        await saveArenaWriteUp(eventName, challengeId, writeUp);
      }
      await submitArena(eventName, challengeId);
      setActiveTab('verdict');
    } catch (e) {
      // ApiClient already surfaced the backend message (min length, cap, in-flight) as a flashbar.
      if (isConflict(e)) {
        addErrorFlashbar(t(i18nKeys.myJams.challenges.details.arena.writeUp.conflict));
      }
      LoggingService.debug('Arena submit failed', e);
    }
  };

  if (!projection) {
    return null;
  }

  const keys = i18nKeys.myJams.challenges.details.arena;
  const maxPoints = projection.scoringReference?.maxPoints ?? selectedJamChallenge?.possiblePoints ?? 0;
  const dimensionName = (key: string) => projection.dimensions.find((d) => d.key === key)?.name ?? key;
  const lastEditedBy = arenaWriteUpState?.lastEditedBy ?? team?.lastEditedBy;

  const submissionsLine = arenaSubmitResult
    ? t(keys.writeUp.submissionsRemaining, { count: arenaSubmitResult.submissionsRemaining })
    : t(keys.writeUp.submissionsMade, { count: team?.submissionCount ?? 0 });

  const status: { type: StatusIndicatorProps.Type; label: string } = !arenaVerdict
    ? { type: 'stopped', label: t(keys.status.notSubmitted) }
    : isPending
    ? { type: 'in-progress', label: t(keys.status.judging) }
    : arenaVerdict.status === JamArenaVerdictStatus.ERROR
    ? { type: 'error', label: t(keys.status.error) }
    : { type: 'success', label: t(keys.status.scored) };

  const latestScore =
    arenaVerdict?.status === JamArenaVerdictStatus.SCORED
      ? t(keys.verdict.points, { earned: arenaVerdict.grossPoints ?? 0, possible: maxPoints })
      : t(keys.summary.none);

  const summary = (
    <Container className="dark-bg" data-testid="arena-summary">
      <ColumnLayout columns={4} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">{t(keys.summary.status)}</Box>
          <StatusIndicator type={status.type}>{status.label}</StatusIndicator>
        </div>
        <div>
          <Box variant="awsui-key-label">{t(keys.summary.latestScore)}</Box>
          <div>{latestScore}</div>
        </div>
        <div>
          <Box variant="awsui-key-label">{t(keys.summary.submissions)}</Box>
          <div data-testid="arena-submissions">{submissionsLine}</div>
        </div>
        <div>
          <Box variant="awsui-key-label">{t(keys.summary.lastSaved)}</Box>
          <div>{lastEditedBy ?? t(keys.summary.none)}</div>
        </div>
      </ColumnLayout>
    </Container>
  );

  const brief = (
    <SpaceBetween direction="vertical" size="l">
      <Alert type="info" header={t(keys.scenario.howItWorks.title)} data-testid="arena-how-it-works">
        <ol>
          <li>{t(keys.scenario.howItWorks.explain)}</li>
          <li>{t(keys.scenario.howItWorks.build)}</li>
          <li>{t(keys.scenario.howItWorks.submit)}</li>
        </ol>
      </Alert>
      <Container
        className="dark-bg"
        header={<Header variant="h2">{t(keys.scenario.title)}</Header>}
        data-testid="arena-scenario"
      >
        <SpaceBetween direction="vertical" size="m">
          <Markdown content={projection.scenarioBrief} />
          <Box>
            <Box variant="h3">{t(keys.scenario.objective)}</Box>
            <Markdown content={projection.objective} />
          </Box>
        </SpaceBetween>
      </Container>
      <Container className="dark-bg" header={<Header variant="h2">{t(keys.scenario.requirements)}</Header>}>
        <SpaceBetween direction="vertical" size="s">
          {projection.requirements.map((requirement) => (
            <SpaceBetween key={requirement.id} direction="horizontal" size="xs" alignItems="center">
              <Badge color={requirement.criticality === 'HARD' ? 'red' : 'grey'}>
                {requirement.criticality === 'HARD' ? t(keys.scenario.hard) : t(keys.scenario.standard)}
              </Badge>
              <Box variant="strong">{requirement.id}</Box>
              <Box variant="span">{requirement.statement}</Box>
            </SpaceBetween>
          ))}
        </SpaceBetween>
      </Container>
      <Container
        className="dark-bg"
        header={
          <Header variant="h2" description={t(keys.scenario.dimensions)}>
            {t(keys.scenario.scoring)}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="s">
          {projection.dimensions.map((dimension) => (
            <ExpandableSection
              key={dimension.key}
              headerText={dimension.name}
              headerDescription={`${t(keys.scenario.weight, { weight: dimension.weight })} · ${t(keys.scenario.upTo, {
                points: Math.round((dimension.weight * maxPoints) / 100),
              })}`}
            >
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">{t(keys.scenario.rubric.below)}</Box>
                  <div>{dimension.rubric?.below}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">{t(keys.scenario.rubric.acceptable)}</Box>
                  <div>{dimension.rubric?.acceptable}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">{t(keys.scenario.rubric.excellent)}</Box>
                  <div>{dimension.rubric?.excellent}</div>
                </div>
              </ColumnLayout>
            </ExpandableSection>
          ))}
        </SpaceBetween>
      </Container>
      <Box float="right">
        <Button
          variant="primary"
          iconName="angle-right"
          iconAlign="right"
          data-testid="arena-to-write-up"
          onClick={() => setActiveTab('writeUp')}
        >
          {t(keys.nav.toWriteUp)}
        </Button>
      </Box>
    </SpaceBetween>
  );

  const fieldLabels: Record<keyof JamArenaWriteUp, { label: string; placeholder: string }> = {
    architecture: { label: t(keys.writeUp.architecture), placeholder: t(keys.writeUp.architecturePlaceholder) },
    decisions: { label: t(keys.writeUp.decisions), placeholder: t(keys.writeUp.decisionsPlaceholder) },
    requirementsCoverage: {
      label: t(keys.writeUp.requirementsCoverage),
      placeholder: t(keys.writeUp.requirementsCoveragePlaceholder),
    },
  };

  const writeUpTab = (
    <SpaceBetween direction="vertical" size="l">
      {!isSelectedChallengeStarted && <Alert type="info">{t(keys.writeUp.notStarted)}</Alert>}
      <Container
        className="dark-bg"
        header={
          <Header
            variant="h2"
            description={t(keys.writeUp.description)}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  data-testid="arena-save"
                  disabled={!isSelectedChallengeStarted || !dirty || isSubmittingArena}
                  loading={isSavingArenaWriteUp}
                  onClick={() => void onSave()}
                >
                  {t(keys.writeUp.save)}
                </Button>
                <Button
                  data-testid="arena-submit"
                  variant="primary"
                  disabled={!isSelectedChallengeStarted || isPending || isSavingArenaWriteUp}
                  loading={isSubmittingArena}
                  onClick={() => void onSubmit()}
                >
                  {t(keys.writeUp.submit)}
                </Button>
              </SpaceBetween>
            }
          >
            {t(keys.writeUp.title)}
          </Header>
        }
        data-testid="arena-write-up"
      >
        <SpaceBetween direction="vertical" size="m">
          {WRITE_UP_FIELDS.map(({ field, testId }) => (
            <FormField
              key={field}
              stretch
              label={fieldLabels[field].label}
              description={
                field === 'requirementsCoverage'
                  ? t(keys.writeUp.requirementsHint, { ids: projection.requirements.map((r) => r.id).join(', ') })
                  : undefined
              }
              constraintText={t(keys.writeUp.characters, { count: (writeUp[field] ?? '').length })}
            >
              <Textarea
                data-testid={testId}
                rows={6}
                value={writeUp[field] ?? ''}
                placeholder={fieldLabels[field].placeholder}
                onChange={({ detail }) => update(field)(detail.value)}
                disabled={!isSelectedChallengeStarted}
              />
            </FormField>
          ))}
        </SpaceBetween>
      </Container>
      <Button iconName="angle-left" data-testid="arena-to-brief" onClick={() => setActiveTab('brief')}>
        {t(keys.nav.toBrief)}
      </Button>
    </SpaceBetween>
  );

  const verdictTab = (
    <Container
      className="dark-bg"
      header={
        <Header
          variant="h2"
          description={arenaVerdict ? t(keys.verdict.submission, { number: arenaVerdict.submissionNumber }) : undefined}
          actions={
            arenaVerdict && !isPending ? (
              <Button data-testid="arena-revise" onClick={() => setActiveTab('writeUp')}>
                {t(keys.nav.revise)}
              </Button>
            ) : undefined
          }
        >
          {t(keys.verdict.title)}
        </Header>
      }
      data-testid="arena-verdict"
    >
      {!arenaVerdict && <Box color="text-body-secondary">{t(keys.verdict.noVerdict)}</Box>}
      {isPending && (
        <SpaceBetween direction="horizontal" size="s" data-testid="arena-verdict-pending">
          <Spinner />
          <Box>{t(keys.verdict.judging)}</Box>
        </SpaceBetween>
      )}
      {arenaVerdict?.status === JamArenaVerdictStatus.ERROR && (
        <Alert type="error" header={t(keys.verdict.errorTitle)}>
          {arenaVerdict.error} {t(keys.verdict.errorFree)}
        </Alert>
      )}
      {arenaVerdict?.status === JamArenaVerdictStatus.SCORED && (
        <SpaceBetween direction="vertical" size="m" data-testid="arena-verdict-scored">
          {arenaVerdict.evidenceUnavailable && <Alert type="warning">{t(keys.verdict.evidenceUnavailable)}</Alert>}
          <ProgressBar
            label={t(keys.verdict.total)}
            value={Math.round((arenaVerdict.totalFraction ?? 0) * 100)}
            additionalInfo={t(keys.verdict.points, { earned: arenaVerdict.grossPoints ?? 0, possible: maxPoints })}
          />
          {(arenaVerdict.dimensions ?? []).map((dimension) => (
            <ExpandableSection
              key={dimension.key}
              variant="container"
              headerText={dimensionName(dimension.key)}
              headerCounter={`${Math.round(dimension.score * 100)}%`}
            >
              <SpaceBetween direction="vertical" size="s">
                <ProgressBar value={Math.round(dimension.score * 100)} />
                <Box variant="small" color="text-body-secondary">
                  {t(keys.verdict.reasoning)}
                </Box>
                <Box variant="p">{dimension.reasoning}</Box>
              </SpaceBetween>
            </ExpandableSection>
          ))}
        </SpaceBetween>
      )}
    </Container>
  );

  return (
    <SpaceBetween direction="vertical" size="l">
      {summary}
      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId as ArenaTab)}
        tabs={[
          { id: 'brief', label: t(keys.tabs.brief), content: brief },
          { id: 'writeUp', label: t(keys.tabs.writeUp), content: writeUpTab },
          { id: 'verdict', label: t(keys.tabs.verdict), content: verdictTab },
        ]}
      />
    </SpaceBetween>
  );
};

export default JamArenaPanel;
